import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { ClienteLista } from "../../domain/models/clienteLista";
import { initialClientesListaFiltersValues, useClientesListaFilters } from "../hooks/clientes_lista/filters";
import { useClientesListaPagination } from "../hooks/clientes_lista/pagination";

type LoadClientesListaState = {
    idsCentroCusto?: number[],
    nomeFantasia?: string,
    cnpj?: string,
    cpf?: string,
    unidade?: string,

    page?: number;
    rowsPerPage?: number;
}

export const useClientesListaViewModel = () => {

    const {
        comercial: {
            deleteClienteUseCase,
            getAllClienteUseCase,
        },
        commons: {
            getCentroCustoDropDownUseCase,
        },

    } = useDependencyInjection();


    const navigate = useNavigate();
    const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const debouncedRef = useCreateDebounceHandler(1500);

    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    const [selectedIdCliente, setSelectedIdCliente] = useState<number>();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [clientesLista, setClientesLista] = useState<ClienteLista[]>();
    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();

    const filters = useClientesListaFilters();
    const pagination = useClientesListaPagination();


    //#region CARREGAMENTO.
    const loadClientesLista = useCallback(async (funcState?: LoadClientesListaState) => {

        try {
            const filteredIdsCentroCusto = funcState?.idsCentroCusto ?? filters.idsCentroCusto;
            const filteredNomeFantasia = funcState?.nomeFantasia ?? filters.nomeFantasia;
            const filteredCnpj = funcState?.cnpj ?? filters.cnpj;
            const filteredCpf = funcState?.cpf ?? filters.cpf;
            const filteredUnidade = funcState?.unidade ?? filters.unidade;

            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllClienteUseCase.execute(
                filteredIdsCentroCusto,
                filteredNomeFantasia,
                filteredCnpj,
                filteredCpf,
                filteredUnidade,

                filteredPage,
                filteredRowsPerPage,
            );

            setClientesLista(getAllClienteUseCase.items);

            pagination.setRowsPerPage(getAllClienteUseCase.pageSize);
            pagination.setTotalItems(getAllClienteUseCase.totalItems);
        } catch (e: unknown) {
            setClientesLista([]);
            pushErrorNotification("Error", processError(e, "Falha ao buscar lista de clientes!"));
        }
    }, [
        filters,
        pagination,
        getAllClienteUseCase,
        pushErrorNotification
    ]);


    const loadCentroCustoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const response = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(response);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar Centro de Custo!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        getCentroCustoDropDownUseCase,
        setPageLoading,
        pushErrorNotification
    ]);


    //#endregion

    usePageStartBootstrap(async () => {
        try {
            if (clientesLista === undefined) {
                await loadClientesLista();
                await loadCentroCustoDropDown();
            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de clientes!")[0]);
        }
    });




    //#region NOVO/EDITAR.

    const handleNewClientes = useCallback(() => {
        navigate("/Clientes_cadastro");
    }, [navigate]);

    const handleEditClientes = useCallback((idClientes: number | null) => {
        navigate("/Clientes_cadastro", { state: { idClientes } });
    }, [navigate]);

    //#endregion



    //#region PAGINAÇÃO.


    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadClientesLista();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de clientes!'));
        } finally {
            setLoading(false);
        }
    }, [loadClientesLista]);


    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadClientesLista({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de clientes!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadClientesLista]);


    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadClientesLista({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de clientes!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadClientesLista]);

    //#endregion






    //#region DELECAO

    const handleClientesDeleteOpenState = useCallback((idCliente: number | null, openState: boolean) => {
        if (openState) {
            if (idCliente) {
                setSelectedIdCliente(idCliente);
                setOpenDeleteModal(true);
            } else {
                setSelectedIdCliente(undefined);
                setOpenDeleteModal(false);
            }
        }
        setOpenDeleteModal(openState);
    }, []);


    const handleConfirmarDeletarClientes = useCallback(async () => {
        try {

            setSaving(true);
            if (selectedIdCliente) {
                await deleteClienteUseCase.execute(selectedIdCliente);
                pushInfoNotification("Sucesso", " Cliente deletado com sucesso!", { isAutoFinish: true });
            } else {
                pushErrorNotification("Erro", "Não foi possivel deletar Cliente!", { isAutoFinish: true });

            }

            await loadCentroCustoDropDown()
            await loadClientesLista()
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Deletar Cliente!"));
        } finally {
            setSaving(false);
            setOpenDeleteModal(false);
            setSelectedIdCliente(undefined)
        }
    }, [
        selectedIdCliente,
        deleteClienteUseCase,
        loadCentroCustoDropDown,
        loadClientesLista,
        pushErrorNotification,
        pushInfoNotification]);


    //#endregion





    //#region PESQUISA.

    const handleIdsCentroCustoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setIdsCentroCusto(newValues)
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadClientesLista({ idsCentroCusto: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar clientes!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadClientesLista]
    );

    const handleNomeFantasiaChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {

            filters.setNomeFantasia(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadClientesLista({ nomeFantasia: newValue ?? undefined, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar clientes!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadClientesLista, pushErrorNotification]
    );


    const handleCpfChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {

            filters.setCpf(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadClientesLista({ cpf: newValue ?? undefined, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar clientes!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadClientesLista, pushErrorNotification]
    );

    const handleCnpjChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {

            filters.setCnpj(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadClientesLista({ cnpj: newValue ?? undefined, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar clientes!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadClientesLista, pushErrorNotification]);

    const handleUnidadeChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {

            filters.setUnidade(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadClientesLista({ unidade: newValue ?? undefined, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar clientes!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadClientesLista, pushErrorNotification]);

    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadClientesLista({
                ...initialClientesListaFiltersValues,
                page: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar clientes!'));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        pagination,
        loadClientesLista,
        pushErrorNotification]);

    //#endregion 

    return {
        loading,
        saving,
        clientesLista: clientesLista ?? [],
        errorMessage,
        filters,
        pagination,
        openDeleteModal,
        ddlCentroCusto: ddlCentroCusto ?? [],
        selectedIdCliente,

        handleUnidadeChange,
        handleNewClientes,
        handleEditClientes,
        handleIdsCentroCustoChange,
        handleNomeFantasiaChange,
        handleCpfChange,
        handleCnpjChange,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleClearFilters,
        handleClientesDeleteOpenState,
        handleConfirmarDeletarClientes

    };
}


